import { useMemo, useState } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { IconChevronLeft, IconShare } from '@tabler/icons-react';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import { MOBILE_CAROUSEL_BREAKPOINT_TIERS } from 'components/marketplace/helpers/presizedPhotos';
import { useTrackProperty } from 'components/marketplace/helpers/useTrackProperty';
import { Carousel } from 'components/marketplace/shared/Carousel';
import { CarouselDots } from 'components/marketplace/shared/CarouselDots';
import { GalleryListingPhoto } from 'components/marketplace/shared/GalleryListingPhoto';

import { MobileModalContent } from './MobileModalContent';

export enum ModalContent {
  PHOTOS = 'photos',
  MAP = 'map',
}

interface PhotoCarouselProps {
  photos: GalleryPhoto[] | null | undefined;
  address?: string | null;
  price?: number | null;
  toggleLightbox?: () => void;
  setSelectedPhotoIndex?: (idx: number) => void;
  latitude?: number | null;
  longitude?: number | null;
  openShareModal: () => void;
}

const MAP_MARKER_ICON =
  'https://images.opendoor.com/source/s3/imgdrop-production/2024-06-1719508347054-73258.png?preset=square-128';

export const PhotoCarousel = ({
  photos,
  address,
  price,
  toggleLightbox,
  setSelectedPhotoIndex,
  latitude,
  longitude,
  openShareModal,
}: PhotoCarouselProps) => {
  const { trackProperty } = useTrackProperty();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<ModalContent>(ModalContent.PHOTOS);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Floor plans are portrait/non-3:2, and these slides cover-crop, which would
  // mangle them. Keep floor plans out of the slide carousel; they still surface
  // uncropped in the "see all photos" modal (MobileModalContent) and Lightbox.
  const carouselPhotos = useMemo(
    () => photos?.filter((photo) => !photo.isFloorPlan) ?? [],
    [photos],
  );

  const getCarouselPhotoAlt = (idx: number) =>
    `Property photo ${idx + 1}${address ? ` of ${address}` : ''}`;

  const togglePhotoModal = () => {
    trackProperty('property-images', 'marketplace-pdp-mobile-gallery');
    setIsModalOpen((prev) => !prev);
    setModalContent(ModalContent.PHOTOS);
  };

  const toggleMapModal = () => {
    setIsModalOpen((prev) => !prev);
    setModalContent(ModalContent.MAP);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openLightbox = (selectedImageIndex: number) => {
    trackProperty('property-images', 'marketplace-pdp-lightbox');
    setSelectedPhotoIndex && setSelectedPhotoIndex(selectedImageIndex);
    toggleLightbox && toggleLightbox();
  };

  return (
    <Box className="w-full md:hidden">
      <Carousel
        count={carouselPhotos.length}
        index={currentImageIndex}
        onIndexChange={setCurrentImageIndex}
        ariaLabel="Property photos"
        className="aspect-square w-full"
        renderSlide={(i) => {
          const photo = carouselPhotos[i];
          if (!photo) {
            return null;
          }
          return (
            <GalleryListingPhoto
              photo={photo}
              sizes={MOBILE_CAROUSEL_BREAKPOINT_TIERS}
              lazy={i !== 0}
              preload={i === 0}
              fetchPriority={i === 0 ? 'high' : undefined}
              alt={getCarouselPhotoAlt(i)}
              objectFit="cover"
            />
          );
        }}
      >
        <Button
          variant="secondary"
          aria-label="back"
          analyticsName="marketplace-carousel-back-button"
          onClick={() => history.back()}
          className="absolute left-6 top-6 z-[1] size-9 rounded-full border-od-border-tertiary p-0"
        >
          <IconChevronLeft size={24} />
        </Button>
        <Button
          variant="secondary"
          aria-label="share"
          analyticsName="marketplace-carousel-share-button"
          onClick={openShareModal}
          className="absolute right-6 top-6 z-[1] size-9 rounded-full border-od-border-tertiary p-0"
        >
          <IconShare size={24} />
        </Button>
        {latitude && longitude && (
          <Box
            aria-label="Click to view map"
            onClick={toggleMapModal}
            analyticsName="marketplace-carousel-map-button"
            className="absolute bottom-5 right-6 z-[1] size-16 active:scale-95"
          >
            <Image alt="View property on map" src={MAP_MARKER_ICON} className="size-16" />
          </Box>
        )}
        <Box
          className="pointer-events-none absolute bottom-5 left-0 right-0 z-[1] items-center justify-center"
          aria-hidden="true"
        >
          <CarouselDots length={carouselPhotos.length} currentIndex={currentImageIndex} />
        </Box>
        {/* Transparent full-bleed tap target that opens the gallery modal.
            No z-index, so the z-[1] controls above stay clickable. */}
        <Box
          aria-label="Click to view more photos"
          onClick={togglePhotoModal}
          analyticsName="marketplace-carousel-photo-clicked"
          className="absolute inset-0 h-full w-full bg-transparent p-0"
        />
      </Carousel>
      <MobileModalContent
        address={address}
        closeModal={closeModal}
        contentToShow={modalContent}
        isModalOpen={isModalOpen}
        photos={photos}
        price={price}
        latitude={latitude}
        longitude={longitude}
        openLightbox={openLightbox}
        openShareModal={openShareModal}
      />
    </Box>
  );
};
